interface HeroProps {
  scrollToSection: (id: string) => void;
}

const Hero = ({ scrollToSection }: HeroProps) => {
  return (
    <section id="top" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* 点滅アニメーション用スタイル */}
      <style>{`
        @keyframes blink-badge {
          0%, 100% { opacity: 1; box-shadow: 0 0 18px 4px rgba(59,130,246,0.55); }
          50% { opacity: 0.55; box-shadow: 0 0 4px 1px rgba(59,130,246,0.15); }
        }
        .blink-badge {
          animation: blink-badge 5.6s ease-in-out infinite;
        }
        @keyframes blink-text {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .blink-text {
          animation: blink-text 5.6s ease-in-out infinite;
        }
        @keyframes blink-visa {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .blink-visa {
          animation: blink-visa 7s ease-in-out infinite;
        }
      `}</style>

      {/* 背景画像 */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://public.readdy.ai/ai/img_res/e4838118e71e16bcf683c32b75dcd2fe.jpg)'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a1f3a]/95 via-[#0f2d52]/82 to-[#0a1f3a]/90"></div>
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-blue-400/70 to-transparent"></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32">
        <div className="max-w-3xl">
          {/* 上部ラベル */}
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <div className="w-6 md:w-8 h-px bg-blue-400 flex-shrink-0"></div>
            <span className="text-blue-300 text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] font-medium uppercase">
              Gyoseishoshi MK Houmujimusho
            </span>
          </div>

          {/* メインタイトル */}
          <h2
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 md:mb-8 tracking-tight"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            特定技能ビザ申請<br />
            <span className="text-blue-300">専門サポート</span>
          </h2>

          <p className="text-slate-200 text-base md:text-xl leading-relaxed mb-5 md:mb-6">
            大阪を拠点に、登録支援機関・人材紹介会社様の<br className="hidden sm:block" />
            特定技能ビザ申請業務を全面的にサポートいたします。
          </p>

          {/* 営業時間バッジ */}
          <div className="blink-badge inline-flex items-center gap-2 md:gap-3 border-2 border-blue-400 bg-blue-500/20 px-4 md:px-8 py-3 md:py-4 rounded-sm mb-8 md:mb-10 w-full sm:w-auto">
            <i className="ri-time-line text-yellow-400 text-xl md:text-2xl flex-shrink-0"></i>
            <span className="text-yellow-400 text-sm md:text-xl font-black tracking-wide">
              年中無休（不定休）　午前9時〜深夜24時まで対応
            </span>
          </div>

          {/* CTAボタン群 */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4 mb-10 md:mb-12">
            <button
              onClick={() => scrollToSection('contact')}
              className="inline-flex items-center justify-center gap-2 bg-[#1a3a5c] hover:bg-[#0f2d52] text-white px-6 md:px-10 py-4 md:py-5 text-sm md:text-base font-bold tracking-widest transition-all duration-300 whitespace-nowrap cursor-pointer border border-blue-400/50 w-full sm:w-auto"
            >
              <i className="ri-mail-send-line"></i>
              無料でお問い合わせ
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="inline-flex items-center justify-center gap-2 border border-white/60 hover:border-white text-white hover:text-white/80 px-6 md:px-10 py-4 md:py-5 text-base md:text-2xl lg:text-3xl font-black tracking-widest transition-all duration-300 whitespace-nowrap cursor-pointer w-full sm:w-auto"
            >
              <i className="ri-price-tag-3-line"></i>
              報酬額表を確認する
            </button>
          </div>

          {/* 電話番号 */}
          <div className="flex items-center gap-4">
            <div className="w-px h-16 bg-white/20 flex-shrink-0"></div>
            <div className="space-y-2">
              <div>
                <p className="text-slate-300 text-xs tracking-widest mb-0.5">代表電話</p>
                <a
                  href="tel:06-7777-3813"
                  className="text-white text-2xl md:text-3xl font-bold tracking-wider hover:text-blue-300 transition-colors cursor-pointer"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  06-7777-3813
                </a>
              </div>
              <div>
                <p className="text-slate-300 text-xs tracking-widest mb-0.5">代表直通</p>
                <a
                  href="tel:070-9096-1669"
                  className="text-white text-2xl md:text-3xl font-bold tracking-wider hover:text-blue-300 transition-colors cursor-pointer"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  070-9096-1669
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* 右側 - 差別化3カード（xl以上のみ表示） */}
        <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden xl:flex flex-col gap-5">
          {[
            {
              num: '年中無休',
              label: '不定休・深夜24時まで対応',
              icon: 'ri-time-line',
              accent: 'from-blue-500 to-blue-600',
            },
            {
              num: '業界最安値',
              label: '相場の約半額以下の報酬額',
              icon: 'ri-price-tag-3-line',
              accent: 'from-blue-500 to-blue-600',
            },
            {
              num: '3名体制',
              label: '行政書士2名＋事務員1名',
              icon: 'ri-team-line',
              accent: 'from-blue-500 to-blue-600',
            },
          ].map((item, i) => (
            <div
              key={i}
              className="relative overflow-hidden bg-white/10 backdrop-blur-md border border-blue-400/40 px-7 py-5 min-w-[260px]"
            >
              <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${item.accent}`}></div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-500/20 border border-blue-400/40 flex-shrink-0">
                  <i className={`${item.icon} text-blue-300 text-2xl`}></i>
                </div>
                <div>
                  <p className="text-white font-black text-2xl leading-tight tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    {item.num}
                  </p>
                  <p className="text-blue-200 text-sm font-medium mt-0.5">{item.label}</p>
                </div>
              </div>
            </div>
          ))}

          {/* ビザ総合サイトリンク（3名体制カードの下・5cm下） */}
          <a
            href="https://mkhoumujimu-visa.readdy.co/"
            target="_blank"
            rel="noopener noreferrer"
            className="blink-visa relative overflow-hidden bg-white/10 backdrop-blur-md border border-white/40 px-7 py-5 min-w-[260px] cursor-pointer hover:bg-white/20 transition-colors block mt-20"
          >
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-white/60 to-white/20"></div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-white/10 border border-white/30 flex-shrink-0">
                <i className="ri-links-line text-white text-2xl"></i>
              </div>
              <div>
                <p className="text-white font-black text-xl leading-tight tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  行政書士ＭＫ法務事務所
                </p>
                <p className="text-white font-black text-2xl leading-tight tracking-wide mt-0.5" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  ビザ総合サイト
                </p>
              </div>
            </div>
          </a>
        </div>



        {/* モバイル用 特徴カード（xl未満で表示） */}
        <div className="xl:hidden mt-8 grid grid-cols-3 gap-2">
          {[
            { num: '年中無休', label: '深夜24時まで', icon: 'ri-time-line' },
            { num: '最安値', label: '相場の半額以下', icon: 'ri-price-tag-3-line' },
            { num: '3名体制', label: '行政書士2名', icon: 'ri-team-line' },
          ].map((item, i) => (
            <div key={i} className="bg-white/10 border border-blue-400/30 px-3 py-3 text-center">
              <div className="w-8 h-8 flex items-center justify-center bg-blue-500/20 mx-auto mb-2">
                <i className={`${item.icon} text-blue-300 text-base`}></i>
              </div>
              <p className="text-white font-black text-sm leading-tight">{item.num}</p>
              <p className="text-blue-200 text-[10px] mt-0.5">{item.label}</p>
            </div>
          ))}
        </div>

        {/* モバイル用 ビザ総合サイトリンク（3カードの下） */}
        <a
          href="https://mkhoumujimu-visa.readdy.co/"
          target="_blank"
          rel="noopener noreferrer"
          className="blink-visa xl:hidden mt-3 flex items-center gap-3 bg-white/10 border border-white/40 px-4 py-3 cursor-pointer hover:bg-white/20 transition-colors"
        >
          <div className="w-8 h-8 flex items-center justify-center bg-white/10 border border-white/30 flex-shrink-0">
            <i className="ri-links-line text-white text-base"></i>
          </div>
          <div>
            <p className="text-white font-black text-sm leading-tight tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              行政書士ＭＫ法務事務所
            </p>
            <p className="text-white font-black text-base leading-tight tracking-wide mt-0.5" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              ビザ総合サイト
            </p>
          </div>
        </a>


      </div>

      {/* スクロール案内 */}
      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-white/40 text-xs tracking-widest">SCROLL</span>
        <div className="w-px h-8 md:h-10 bg-gradient-to-b from-white/40 to-transparent"></div>
      </div>
    </section>
  );
};

export default Hero;
